/**
 * GTM dataLayer wrapper — the single push point for every marketing
 * tracking event (GA4 page views, Google Ads conversions, future
 * Meta/X/Reddit pixels once Phase 2 lands).
 *
 * Design rules:
 *   * Semantic events ("signup_started", "pilot_application_submitted")
 *     not URL-based triggers. GTM maps each event to one-or-many
 *     destinations (GA4 + Google Ads native + future pixels) without
 *     touching this file.
 *   * Surface-gated. Only fires on the marketing surface
 *     (nestplot.com / www.nestplot.com) — never on the operator app,
 *     the tenant portal, or operator-owned tenant rental sites (per
 *     the operator decision recorded with this PR).
 *   * Every conversion carries an `event_id` so the Phase 3 server-
 *     side CAPI calls can dedupe against the browser pixel that fired
 *     here. Phase 1 doesn't use the id yet — it's wired up now so the
 *     event taxonomy doesn't have to change later.
 *   * PII never enters the dataLayer raw. Email + phone are passed
 *     into the conversion event but Google's tag layer hashes them
 *     SHA-256 before they leave the browser (Enhanced Conversions).
 */

declare global {
  interface Window {
    // Holds both GTM object pushes ({event, …}) and native gtag
    // command tuples (['event','conversion',{…}]).
    dataLayer?: Array<Record<string, unknown> | unknown[]>;
    // Meta Pixel global, present only when a pixel id is configured
    // (marketingTags.ts injectMetaPixel). Undefined otherwise → the
    // Meta firing below no-ops.
    fbq?: (...args: unknown[]) => void;
    // LinkedIn Insight Tag global, present only on the marketing surface
    // once injectLinkedInInsight has run. Undefined otherwise → no-op.
    lintrk?: (action: string, payload?: Record<string, unknown>) => void;
  }
}

/** Meta Pixel standard events for our semantic conversions. Only the
 * mapped events fire a pixel; others push to the dataLayer only. */
const META_EVENT: Partial<Record<ConversionName, string>> = {
  signup_completed: "CompleteRegistration",
  pilot_application_submitted: "Lead",
};

/** Conversion-event taxonomy. Keep this list aligned with the GTM
 * container's trigger names. Adding an event here without a matching
 * trigger in GTM is a silent no-op (the data still lands in the
 * dataLayer history but no destination receives it). */
export type ConversionName =
  | "signup_started"
  | "signup_completed"
  | "pilot_application_submitted"
  | "trial_to_paid"
  | "subscription_upgraded";

export interface BaseEventParams {
  /** Stable per-conversion id. Shared with the Phase 3 server-side
   * CAPI call so destinations dedupe browser-pixel + server-side
   * events. Generated client-side when omitted — the server already
   * stores its own copy on the Operator / Subscription row. */
  event_id?: string;
  /** Optional vertical context — only present when the marketing
   * surface knows it (HeroSignupCard on /for-marina passes
   * "marina"). */
  vertical?: string;
}

export interface SignupConversionParams extends BaseEventParams {
  /** Operator email — Google Ads Enhanced Conversions hashes this
   * SHA-256 in the tag layer; never leaves the browser unhashed.
   * Phase 1: only added to signup_completed because that's the only
   * conversion where we know the address with certainty. */
  email?: string;
  phone?: string;
}

let _enabled = false;

/** Call once on app boot from a marketing-surface context. Pushes a
 * sentinel into the dataLayer so we can see in DebugView whether the
 * SPA actually opted in (vs. the GTM snippet running on a surface
 * that should never have loaded it). */
export function enable(): void {
  if (_enabled) return;
  _enabled = true;
  push("analytics_ready", {});
}

/** Internal — every other helper goes through here. No-op on
 * surfaces where enable() was never called, so a stray track() call
 * from operator-app code doesn't accidentally leak into Google Ads. */
export function push(event: string, params: Record<string, unknown>): void {
  if (!_enabled) return;
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
}

/** Fires on every route change. Wired up in App.tsx with useLocation.
 * GA4 reads this via a Custom Event trigger named `page_view`. */
export function trackPageView(path: string, title: string): void {
  push("page_view", { page_path: path, page_title: title });
}

/** Google Ads conversion labels (send_to = AW-<id>/<label>) for the
 * account's WEBPAGE conversion actions. Firing these via the native
 * Google tag is what actually registers conversions in Google Ads —
 * the GA4-import path never delivered. Names without a mapping only
 * push to GTM/GA4. */
const ADS_CONVERSION: Partial<Record<ConversionName, string>> = {
  signup_completed: "AW-18014180022/qRiDCLru9bIcELal6o1D",
  pilot_application_submitted: "AW-18014180022/4FhoCI2bmtAcELal6o1D",
};

/** LinkedIn conversion ids on ad account 557809453. These are
 * event-specific (IMAGE_PIXEL) conversions, so they only count when
 * lintrk('track') names them — a URL rule would never fire for an SPA
 * that never reloads the page. */
const LINKEDIN_CONVERSION: Partial<Record<ConversionName, number>> = {
  signup_completed: 30070292,
  trial_to_paid: 30070300,
  subscription_upgraded: 30070300,
};

/** Conversion-event entrypoint. Pushes the semantic event to the
 * dataLayer (GA4/GTM) AND fires the native Google Ads conversion for
 * mapped events. `email`/`phone` ride along for Enhanced Conversions;
 * Google's tag hashes them client-side before they leave the browser. */
export function trackConversion(
  name: ConversionName,
  params: SignupConversionParams = {},
): void {
  const eventId = params.event_id ?? crypto.randomUUID();
  push(name, {
    event_id: eventId,
    vertical: params.vertical,
    user_data: {
      email: params.email,
      phone: params.phone,
    },
  });

  // Fire the native Google Ads conversion. Uses the same dataLayer the
  // Google tag (gtag.js, loaded in marketingTags.ts) drains, so this
  // is a real gtag('event','conversion',…) command. Enhanced
  // Conversions: pass user_data so Google can match hashed email.
  const sendTo = ADS_CONVERSION[name];
  if (_enabled && sendTo && typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push([
      "event",
      "conversion",
      {
        send_to: sendTo,
        ...(params.email ? { user_data: { email: params.email } } : {}),
      },
    ]);
  }

  // Meta Pixel — fires only when a pixel was configured (fbq present).
  // Pass eventID so this browser event dedupes against the server-side
  // Conversions API event the API sends for the same conversion.
  const metaEvent = META_EVENT[name];
  if (_enabled && metaEvent && typeof window !== "undefined" && window.fbq) {
    window.fbq("track", metaEvent, {}, { eventID: eventId });
  }

  // LinkedIn — fires only when the Insight Tag was installed, which only
  // happens on the marketing surface (marketingTags.ts). Unmapped events
  // push to the dataLayer and go no further, same as Meta.
  const liConversion = LINKEDIN_CONVERSION[name];
  if (_enabled && liConversion && typeof window !== "undefined" && window.lintrk) {
    window.lintrk("track", { conversion_id: liConversion });
  }
}
